import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import Popup from 'reactjs-popup';
import Field from 'redux-form/es/Field';
import Button from 'components/common/Button';
import InputField from 'components/Credit24/Core/Controls/InputField';
import InputSelectField from 'components/Credit24/Core/Controls/InputSelectField';
import CheckboxInput from 'components/Credit24/Core/Controls/CheckboxInput';
import { normalizeMsisdn } from 'utils/helpers';
import { statesAU } from 'consts';
import { AUTH_METHOD_IMPERSONATION } from 'consts/loan/authMethodTypes';
import {
  FIELD_NAMES,
  MSISDN_MASK,
  OTP_AND_MSISDN_MASK,
  GRID_ONE_WHALE,
  GRID_ONE_HALF_WITH_PADDING_RIGHT,
  GRID_ONE_HALF_WITH_PADDING_LEFT,
} from './const';
import { propTypes } from './const/propsTypes';
import { onSubmit } from './helpers/onSubmit';

export const UpdateDataModalComponent = ({
  authentication,
  isOpen,
  ocClose,
  updateContactsInfo,
  handleSubmit,
  confirmPhoneNumber,
  msisdn,
}) => {
  const isImpersonate = authentication.authenticationMethod === AUTH_METHOD_IMPERSONATION;
  const [isModalOpen, setIsModalOpen] = useState(isOpen);
  const [isOtpRequired, setIsOtpRequired] = useState(true);
  const [isOtpRequestSended, setIsOtpRequestSended] = useState(false);

  const confirmPhoneNumberAndshowMessage = (phoneNumber) => {
    setIsOtpRequestSended(true);
    confirmPhoneNumber(phoneNumber, null, setIsOtpRequestSended);
  };

  useEffect(() => {
    if (authentication.msisdnPending) {
      confirmPhoneNumberAndshowMessage(authentication.msisdnPending);
    }
  }, []);

  useEffect(() => {
    const isChangeRequestRequired = !authentication.expiredData.verifiedMsisdnChangeRequestExists;
    const shouldOtpRequired = normalizeMsisdn(msisdn) !== authentication.msisdn
      || authentication.msisdnPending
      || isChangeRequestRequired;

    setIsOtpRequired(!shouldOtpRequired);
  }, [msisdn]);

  const closeModal = () => {
    ocClose();
    setIsModalOpen(false);
  };

  return (
    <Popup
      open={isModalOpen}
      closeOnDocumentClick={false}
      className="update-data-popup"
    >
      <div className="update-data-popup-content__container">
        <div className="update-data-popup-content__nav">
          <h2 className="update-data-popup-content__title">
            <FormattedMessage id="update_customer_data.title" />
          </h2>
          {isImpersonate && (
            <a
              className="update-data-popup-content__close"
              onClick={closeModal}
            >
              <div className="popup-configurable__icon-close" />
            </a>
          )}
        </div>
        <div className="update-data-popup-content__form">
          <form
            onSubmit={handleSubmit((data) =>
              onSubmit(data, authentication, updateContactsInfo, setIsModalOpen))}
          >
            <div className="grid grid--full grid--center">
              <h3 className="update-data-popup-content__subtitle">
                <FormattedMessage id="update_customer_data.personal_info.title" />
              </h3>
              <div className={GRID_ONE_WHALE}>
                <Field
                  title={
                    <FormattedMessage id="update_customer_data.field.email" />
                  }
                  name={FIELD_NAMES.email}
                  type="email"
                  component={InputField}
                  isFullWidth
                  isErrorVisible
                  hintConfig={{
                    titleKey: 'update_customer_data.field.email.hint',
                  }}
                />
              </div>
              <div className={GRID_ONE_WHALE}>
                <Field
                  title={
                    <FormattedMessage id="update_customer_data.field.msisdn" />
                  }
                  name={FIELD_NAMES.msisdn}
                  type="tel"
                  component={InputField}
                  mask={MSISDN_MASK}
                  maskChar="_"
                  isMaskedInput
                  isFullWidth
                  hintConfig={{
                    titleKey: 'update_customer_data.field.msisdn.hint',
                  }}
                />
              </div>
              <div className="grid__item two-thirds update-data-popup-content__pr-8">
                <Field
                  title={
                    <FormattedMessage id="update_customer_data.field.otp" />
                  }
                  name={FIELD_NAMES.otp}
                  component={InputField}
                  mask={OTP_AND_MSISDN_MASK}
                  maskChar="_"
                  isFullWidth
                  isMaskedInput
                  disabled={isOtpRequired}
                  hintConfig={{
                    titleKey: 'update_customer_data.field.otp.hint',
                  }}
                />
              </div>
              <div className="grid__item one-third update-data-popup-content__pl-8">
                <Button
                  isLoading={false}
                  onClick={() => confirmPhoneNumberAndshowMessage(normalizeMsisdn(msisdn))}
                  disabled={isOtpRequired || isOtpRequestSended}
                  isFullWidth
                  className="btn--secondary"
                >
                  <FormattedMessage id="update_customer_data.get_otp.button" />
                </Button>
              </div>
              { isOtpRequestSended && (
                <p className="update-data-popup-content__message">
                  <FormattedMessage id="update_customer_data.otp_message" />
                </p>
              )}
              { !isOtpRequestSended && (
                <div className="grid__item one-third update-data-popup-content__pl-8">
                  <Button
                    isLoading={false}
                    onClick={closeModal}
                    disabled={isOtpRequestSended}
                    isFullWidth
                    className="btn--secondary"
                  >
                    <FormattedMessage id="update_customer_data.skip_otp.button" />
                  </Button>
                </div>
              )}
              <h3 className="update-data-popup-content__subtitle update-data-popup-content__subtitle--secondary">
                <FormattedMessage id="update_customer_data.address.title" />
              </h3>
              <div className={GRID_ONE_HALF_WITH_PADDING_RIGHT}>
                <Field
                  title={
                    <FormattedMessage id="update_customer_data.field.street" />
                  }
                  name={FIELD_NAMES.street}
                  type="text"
                  component={InputField}
                  isFullWidth
                  isErrorVisible
                />
              </div>
              <div className={GRID_ONE_HALF_WITH_PADDING_LEFT}>
                <Field
                  title={
                    <FormattedMessage id="update_customer_data.field.streetNumber" />
                  }
                  name={FIELD_NAMES.streetNumber}
                  type="text"
                  component={InputField}
                  isFullWidth
                  isErrorVisible
                />
              </div>
              <div className={GRID_ONE_HALF_WITH_PADDING_RIGHT}>
                <Field
                  title={
                    <FormattedMessage id="update_customer_data.field.unitNumber" />
                  }
                  name={FIELD_NAMES.unitNumber}
                  type="text"
                  component={InputField}
                  isFullWidth
                  isErrorVisible
                />
              </div>
              <div className={GRID_ONE_HALF_WITH_PADDING_LEFT}>
                <Field
                  title={
                    <FormattedMessage id="update_customer_data.field.city" />
                  }
                  name={FIELD_NAMES.city}
                  type="text"
                  component={InputField}
                  isFullWidth
                  isErrorVisible
                />
              </div>
              <div className={GRID_ONE_HALF_WITH_PADDING_RIGHT}>
                <Field
                  emptyTextKey="update_customer_data.field.state"
                  name={FIELD_NAMES.province}
                  component={InputSelectField}
                  options={statesAU}
                  isFullWidth
                  isErrorVisible
                />
              </div>
              <div className={GRID_ONE_HALF_WITH_PADDING_LEFT}>
                <Field
                  title={
                    <FormattedMessage id="update_customer_data.field.postCode" />
                  }
                  name={FIELD_NAMES.postcode}
                  type="text"
                  component={InputField}
                  mask={OTP_AND_MSISDN_MASK}
                  maskChar="_"
                  isFullWidth
                  isMaskedInput
                  isErrorVisible
                />
              </div>
              <div className="grid__item one-whole update-data-popup-content__checkbox">
                <Field
                  name={FIELD_NAMES.checkbox}
                  component={CheckboxInput}
                  text={<FormattedMessage id="update_customer_data.checkbox" />}
                  mixins="checkbox--small"
                />
              </div>
              <div className="grid__item one-half palm--one-half">
                <Button type="submit" isLoading={false} isFullWidth>
                  <FormattedMessage id="update_customer_data.submit" />
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Popup>
  );
};

UpdateDataModalComponent.propTypes = propTypes;
